import { apiClient } from '../config';
import { endpoints } from '../endpoints';

/**
 * Authentication service for handling user authentication operations
 */
export const authService = {
  /**
   * Register a new user
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.firstName - User first name
   * @param {string} userData.lastName - User last name
   * @param {string} [userData.affiliation] - User affiliation (optional)
   * @param {string} [userData.orcidId] - User ORCID ID (optional)
   * @param {string} [userData.expertiseAreas] - User expertise areas (optional)
   * @param {string} userData.countryId - User country ID
   * @param {string} [userData.stateId] - User state ID (optional)
   * @returns {Promise<Object>} - Promise resolving to user and token data
   */
  register: async (userData) => {
    const response = await apiClient.post(endpoints.auth.register, userData);
    return response.data;
  },

  /**
   * Login a user
   * @param {Object} credentials - User login credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} - Promise resolving to user and token data
   */
  login: async (credentials) => {
    const response = await apiClient.post(endpoints.auth.login, credentials);
    return response.data;
  },

  /**
   * Refresh the authentication token
   * @param {string} refreshToken - The refresh token
   * @returns {Promise<Object>} - Promise resolving to new token data
   */
  refreshToken: async (refreshToken) => {
    const response = await apiClient.post(endpoints.auth.refreshToken, { refreshToken });
    return response.data;
  },

  /**
   * Change user password
   * @param {Object} passwordData - Password change data
   * @param {string} passwordData.currentPassword - Current password
   * @param {string} passwordData.newPassword - New password
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  changePassword: async (passwordData) => {
    const response = await apiClient.post(endpoints.auth.changePassword, passwordData);
    return response.data;
  },

  /**
   * Get current user profile
   * @returns {Promise<Object>} - Promise resolving to user profile data
   */
  getProfile: async () => {
    const response = await apiClient.get(endpoints.auth.profile);
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await apiClient.put(endpoints.auth.profile, profileData);
    return response.data;
  },

  /**
   * Upload user profile picture
   * @param {FormData} formData - Form data containing the profile picture file
   * @returns {Promise<Object>} - Promise resolving to updated profile data with picture URL
   */
  uploadProfilePicture: async (formData) => {
    const response = await apiClient.post(endpoints.auth.uploadProfilePicture, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  /**
   * Logout user (client-side only)
   */
  logout: async (accessToken) => {
    if (!accessToken) return;
    await apiClient.post(endpoints.auth.logout, undefined, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
  }
};
